import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Settings,
  Shield,
  Calendar,
  Clock,
  ExternalLink,
  Link2,
  Gamepad2,
  Eye,
  EyeOff,
  ChevronRight,
  Zap,
} from 'lucide-react';

import { Avatar, Badge, Button, Tooltip } from '../../components/ui';
import { PlatformIcon, getPlatformProfileUrl } from '../../components/icons/PlatformIcon';
import { trpc } from '../../lib/trpc';

export const Route = createFileRoute('/app/profile')({
  staticData: { title: 'Profile' },
  component: ProfilePage,
});

const platformConfig: Record<string, { label: string; color: string }> = {
  riot: { label: 'Riot Games', color: 'from-red-500/20 to-red-600/5' },
  steam: { label: 'Steam', color: 'from-blue-500/20 to-blue-600/5' },
  microsoft: { label: 'Microsoft', color: 'from-green-500/20 to-green-600/5' },
  epic: { label: 'Epic Games', color: 'from-gray-400/20 to-gray-500/5' },
  battlenet: { label: 'Battle.net', color: 'from-blue-400/20 to-blue-500/5' },
};

function formatRelativeDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function ProfilePage() {
  const { data: user, isLoading: userLoading } = trpc.user.me.useQuery();
  const { data: accounts, isLoading: accountsLoading } = trpc.linkedAccount.list.useQuery();
  const navigate = useNavigate();

  if (userLoading) return <ProfileSkeleton />;
  if (!user) return null;

  const statusMap = {
    online: { label: 'Online', color: 'green' as const, dotClass: 'bg-green-500' },
    offline: { label: 'Offline', color: 'gray' as const, dotClass: 'bg-surface-500' },
    in_game: { label: 'In Game', color: 'blue' as const, dotClass: 'bg-blue-500' },
    in_queue: { label: 'In Queue', color: 'yellow' as const, dotClass: 'bg-yellow-500' },
  };
  const status = statusMap[user.status] ?? statusMap.offline;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      {/* Hero Banner */}
      <div className="relative rounded-2xl overflow-hidden mb-8 animate-fade-up">
        {/* Gradient background with noise */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/60 via-surface-800 to-surface-900 noise-overlay" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-500/5 rounded-full blur-3xl" />

        {/* Geometric accent line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />

        <div className="relative px-8 pt-10 pb-8">
          {/* Top row: visibility + settings */}
          <div className="flex justify-end gap-2 mb-6">
            <Badge
              color={user.isPublic ? 'brand' : 'gray'}
              variant="outline"
              size="sm"
            >
              {user.isPublic ? (
                <><Eye className="size-3 mr-1" /> Public Profile</>
              ) : (
                <><EyeOff className="size-3 mr-1" /> Private Profile</>
              )}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Settings className="size-4" />}
              onClick={() => navigate({ to: '/app/settings', search: { tab: 'profile' } })}
            >
              Settings
            </Button>
          </div>

          {/* Main profile row */}
          <div className="flex items-end gap-6">
            {/* Avatar with glow ring */}
            <div className="relative shrink-0">
              <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-brand-500/40 to-brand-700/20 blur-sm" />
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-brand-500/60 to-brand-700/30" />
              <div className="relative">
                <Avatar
                  src={user.avatarUrl}
                  initials={user.username.slice(0, 2)}
                  size="xl"
                  bordered={false}
                />
              </div>
              {/* Status dot — overlaid on avatar */}
              <div className={`absolute -bottom-0.5 -right-0.5 size-4 ${status.dotClass} rounded-full border-[3px] border-surface-800`} />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-display font-bold tracking-tight truncate">
                  {user.username}
                </h1>
                <Badge color={status.color} variant="solid" size="sm">
                  {status.label}
                </Badge>
              </div>

              {user.bio ? (
                <p className="text-surface-200 text-sm leading-relaxed max-w-lg mb-3">
                  {user.bio}
                </p>
              ) : (
                <p className="text-surface-400 text-sm italic mb-3">
                  No bio set
                </p>
              )}

              {/* Meta chips */}
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-surface-300 bg-surface-800/80 px-2.5 py-1 rounded-full">
                  <Calendar className="size-3" />
                  Joined {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                {user.lastOnlineAt && user.status === 'offline' && (
                  <span className="flex items-center gap-1.5 text-surface-300 bg-surface-800/80 px-2.5 py-1 rounded-full">
                    <Clock className="size-3" />
                    Last seen {formatRelativeDate(user.lastOnlineAt)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linked Accounts — takes 2 cols */}
        <div className="lg:col-span-2 animate-fade-up animation-delay-100" style={{ opacity: 0 }}>
          <div className="rounded-2xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
            {/* Section header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-700/30">
              <div className="flex items-center gap-2.5">
                <div className="size-8 rounded-lg bg-brand-500/10 flex items-center justify-center">
                  <Gamepad2 className="size-4 text-brand-400" />
                </div>
                <div>
                  <h2 className="font-display font-semibold text-sm tracking-wide uppercase">
                    Game Accounts
                  </h2>
                  <p className="text-xs text-surface-400">
                    {accounts ? `${accounts.length} linked` : 'Loading...'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                rightIcon={<ChevronRight className="size-3.5" />}
                onClick={() => navigate({ to: '/app/settings', search: { tab: 'accounts' } })}
              >
                Manage
              </Button>
            </div>

            {/* Account list */}
            <div className="p-3">
              {accountsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-[72px] rounded-xl bg-surface-700/30 animate-pulse" />
                  ))}
                </div>
              ) : accounts && accounts.length > 0 ? (
                <div className="space-y-1.5">
                  {accounts.map((account, i) => {
                    const config = platformConfig[account.platform] ?? {
                      label: account.platform,
                      color: 'from-surface-600/20 to-surface-700/5',
                    };

                    return (
                      <div
                        key={account.id}
                        className="group relative flex items-center gap-4 px-4 py-3.5 rounded-xl bg-gradient-to-r hover:bg-surface-700/30 transition-all duration-200 cursor-default"
                        style={{
                          animationDelay: `${(i + 2) * 100}ms`,
                        }}
                      >
                        {/* Platform icon */}
                        <div className={`size-11 rounded-xl bg-gradient-to-br ${config.color} border border-white/5 flex items-center justify-center shrink-0`}>
                          <PlatformIcon platform={account.platform} className="size-5 text-white" />
                        </div>

                        {/* Account info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm truncate">
                              {account.platformUsername}
                            </span>
                            {account.platformUsernameDiscriminator && (
                              <span className="text-surface-300 text-sm font-medium">
                                {account.platformUsernameDiscriminator}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-surface-400">{config.label}</span>
                        </div>

                        {/* Verification badge */}
                        {account.verified ? (
                          <Tooltip content="Verified via OAuth" side="left">
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/20">
                              <Shield className="size-3 text-green-400" />
                              <span className="text-xs font-semibold text-green-400">Verified</span>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-700/50 border border-surface-600/30">
                            <span className="text-xs font-medium text-surface-300">Manual</span>
                          </span>
                        )}

                        {/* External link to platform profile */}
                        {(() => {
                          const url = getPlatformProfileUrl(
                            account.platform,
                            account.platformUserId,
                            account.platformUsername,
                          );
                          return url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded hover:bg-surface-700/50 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="size-3.5 text-surface-500 hover:text-surface-200 transition-colors" />
                            </a>
                          ) : (
                            <ExternalLink className="size-3.5 text-surface-500/30" />
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyAccountsState onLink={() => navigate({ to: '/app/settings', search: { tab: 'accounts' } })} />
              )}
            </div>
          </div>
        </div>

        {/* Quick Info Sidebar */}
        <div className="space-y-4 animate-fade-up animation-delay-200" style={{ opacity: 0 }}>
          {/* Quick Stats Card */}
          <div className="rounded-2xl bg-surface-800/50 border border-surface-700/50 p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide uppercase text-surface-300 mb-4">
              Quick Info
            </h3>
            <div className="space-y-3.5">
              <InfoRow
                label="Member Since"
                value={new Date(user.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              />
              <InfoRow
                label="Profile Visibility"
                value={user.isPublic ? 'Public' : 'Private'}
                valueColor={user.isPublic ? 'text-brand-400' : 'text-surface-400'}
              />
              <InfoRow
                label="Status"
                value={status.label}
                valueColor={
                  user.status === 'online' ? 'text-green-400' :
                  user.status === 'in_game' ? 'text-blue-400' :
                  user.status === 'in_queue' ? 'text-yellow-400' :
                  'text-surface-400'
                }
              />
              <InfoRow
                label="Linked Accounts"
                value={accounts ? `${accounts.length}` : '...'}
              />
            </div>
          </div>

          {/* CTA Card */}
          <div className="rounded-2xl bg-gradient-to-br from-brand-500/10 to-brand-600/5 border border-brand-500/20 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="size-4 text-brand-400" />
              <h3 className="font-display font-semibold text-sm text-brand-300">
                Get Competing
              </h3>
            </div>
            <p className="text-xs text-surface-300 leading-relaxed mb-4">
              Join a club and start queuing for matches to build your stats and climb the leaderboard.
            </p>
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => navigate({ to: '/app' })}
            >
              Go to Hub
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueColor = 'text-white',
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-surface-400">{label}</span>
      <span className={`text-sm font-medium ${valueColor}`}>{value}</span>
    </div>
  );
}

function EmptyAccountsState({ onLink }: { onLink: () => void }) {
  return (
    <div className="py-10 px-6 text-center">
      <div className="relative mx-auto mb-4 size-16">
        <div className="absolute inset-0 bg-brand-500/10 rounded-2xl rotate-6" />
        <div className="absolute inset-0 bg-surface-700/50 rounded-2xl -rotate-3" />
        <div className="relative size-full rounded-2xl bg-surface-800 border border-surface-700/50 flex items-center justify-center">
          <Link2 className="size-6 text-surface-400" />
        </div>
      </div>
      <h3 className="font-display font-semibold mb-1">No Accounts Linked</h3>
      <p className="text-sm text-surface-300 max-w-xs mx-auto mb-5">
        Connect your game accounts to display your in-game identity and enable verified matchmaking.
      </p>
      <Button
        variant="outline"
        size="sm"
        leftIcon={<Link2 className="size-4" />}
        onClick={onLink}
      >
        Link Your First Account
      </Button>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      {/* Hero skeleton */}
      <div className="relative rounded-2xl overflow-hidden mb-8 bg-surface-800/50 border border-surface-700/50">
        <div className="px-8 pt-10 pb-8">
          <div className="flex justify-end mb-6">
            <div className="h-7 w-28 bg-surface-700/50 rounded-full animate-pulse" />
          </div>
          <div className="flex items-end gap-6">
            <div className="size-16 rounded-full bg-surface-700 animate-pulse shrink-0" />
            <div className="flex-1 space-y-3 pb-1">
              <div className="h-8 w-56 bg-surface-700 rounded animate-pulse" />
              <div className="h-4 w-80 bg-surface-700/50 rounded animate-pulse" />
              <div className="flex gap-2">
                <div className="h-6 w-28 bg-surface-700/30 rounded-full animate-pulse" />
                <div className="h-6 w-24 bg-surface-700/30 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-2xl bg-surface-800/50 border border-surface-700/50 p-6">
            <div className="h-6 w-40 bg-surface-700 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[72px] rounded-xl bg-surface-700/30 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl bg-surface-800/50 border border-surface-700/50 p-5">
            <div className="h-5 w-24 bg-surface-700 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 w-24 bg-surface-700/30 rounded animate-pulse" />
                  <div className="h-4 w-16 bg-surface-700/30 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
